import { NavLink, useLocation } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';
import {
  LayoutDashboard,
  GitBranch,
  BookOpen,
  ClipboardList,
  Activity,
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Overview', path: ROUTE_PATHS.HOME, icon: LayoutDashboard },
  { label: 'GG-CSAP Pipeline', path: ROUTE_PATHS.PIPELINE, icon: GitBranch },
  { label: 'Codebook', path: ROUTE_PATHS.CODEBOOK, icon: BookOpen },
  { label: 'CISP Run Log', path: ROUTE_PATHS.CISP_LOG, icon: ClipboardList },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-sidebar border-r border-sidebar-border flex flex-col">
        {/* Logo / System ID */}
        <div className="px-5 py-5 border-b border-sidebar-border">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded bg-primary flex items-center justify-center shrink-0">
              <Activity className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <div className="text-sm font-mono font-bold text-sidebar-foreground leading-tight">
                GG-CSAP
              </div>
              <div className="text-[10px] font-mono text-muted-foreground leading-tight tracking-wider">
                PIPELINE v1.0
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          <p className="px-2 pb-2 text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
            Navigation
          </p>
          {NAV_ITEMS.map(({ label, path, icon: Icon }) => {
            const isActive = location.pathname === path;
            return (
              <NavLink
                key={path}
                to={path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-primary font-semibold border border-sidebar-primary/20'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer badge */}
        <div className="px-4 py-4 border-t border-sidebar-border">
          <div className="px-3 py-2 rounded bg-muted/30 border border-border/50">
            <p className="text-[10px] font-mono text-muted-foreground leading-relaxed">
              <span className="text-primary font-semibold">CISP</span> Run Log<br />
              April Assessment · Retro-Entry<br />
              <span className="text-emerald-400">4 sessions loaded</span>
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b border-border bg-card/50 flex items-center px-6 gap-4 shrink-0">
          <div className="flex-1">
            <span className="text-xs font-mono text-muted-foreground">
              GG-CSAP Pipeline · April Model Assessment Codebook
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-[10px] font-mono rounded border border-emerald-700/40 bg-emerald-950/40 text-emerald-400">
              ● SYSTEM ACTIVE
            </span>
            <span className="px-2 py-0.5 text-[10px] font-mono rounded border border-border bg-muted text-muted-foreground">
              2026-04-07
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
}
